import { useFormik } from "formik";
import React, { useEffect } from "react";
import {
  assistantInitialValues,
  assistantValidationSchema,
} from "../../constants/assistant.constants";
import {
  editAssistant,
  getAssistant,
  removeAssistant,
} from "../../features/assistant/assistantSlice";
import { getTwilioDetails } from "../../features/twilio/twilioSlice";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { confirmationModal } from "../../constants/confirmation.constants";
import { Icons } from "../../constants/icon-library";
import { getConfigurationByConfigId } from "../../features/configuration/configurationSlice";

const AssistantDetailsForm = ({
  assistantId,
  user,
  platforms,
  onAssistantUpdated,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  useEffect(() => {
    if (assistantId) {
      dispatch(getConfigurationByConfigId(assistantId));
    }
  }, [assistantId, dispatch]);

  const { configuration = [] } = useSelector((state) => state.configuration);

  const validationSchema = assistantValidationSchema;

  const formik = useFormik({
    initialValues: {
      ...assistantInitialValues,
    },
    validationSchema,
    onSubmit: async (values) => {
      const selectedVoiceConfigObj = configuration?.find(
        (m) => m.uuid === values.selectedVoiceConfiguration,
      );

      const selectedChatConfigObj = configuration?.find(
        (m) => m.uuid === values.selectedChatConfiguration,
      );

      const data = {
        name: values.assistantName,
        voiceConfiguration: selectedVoiceConfigObj?.uuid || null,
        chatConfiguration: selectedChatConfigObj?.uuid || null,
        agentFirstMessage: values.firstMessage,
        platform:
          formik.values.agentType === "platform"
            ? formik.values.selectedPlatformUuid
            : null,
        agentType: formik.values.agentType,
        phoneId: values.phoneId || null,
        accountId: user.org,
      };

      try {
        await dispatch(editAssistant({ uuid: assistantId, data })).unwrap();
        onAssistantUpdated?.();
        toast.success("Assistant updated successfully.");
      } catch {
        toast.error("Failed to update assistant.");
      }
    },
  });

  const handleDelete = async () => {
    const result = await confirmationModal({
      title: "Are you sure?",
      text: "This will permanently delete the assistant.",
    });

    if (result.isConfirmed) {
      try {
        await dispatch(removeAssistant(assistantId)).unwrap();
        navigate(`/my-assistants`);
        toast.success("Assistant deleted successfully.");
      } catch {
        toast.error("Failed to delete assistant.");
      }
    }
  };

  const { setValues } = formik;

  useEffect(() => {
    if (!user.org) return;

    if (window.feather) window.feather.replace();

    dispatch(getTwilioDetails(user.org));

    dispatch(getAssistant(assistantId)).then((res) => {
      const assistant = res.payload;
      console.log("assistant", assistant);
      const phoneId = Array.isArray(assistant.phoneId)
        ? typeof assistant.phoneId[0] === "string"
          ? assistant.phoneId[0]
          : assistant.phoneId[0].uuid
        : assistant.phoneId
          ? typeof assistant.phoneId === "string"
            ? assistant.phoneId
            : assistant.phoneId.uuid
          : "";

      if (assistant) {
        setValues({
          assistantName: assistant.name || "",
          firstMessage:
            assistant.agentFirstMessage || "Hi, How can I help you today?",
          selectedVoiceConfiguration: assistant.voiceConfiguration?.uuid || "",
          selectedChatConfiguration: assistant.chatConfiguration?.uuid || "",
          selectedPlatformUuid: assistant.platform?.uuid || "",
          agentType: assistant.agentType || "platform",
          phoneId: phoneId || "",
        });
      }
    });
  }, [user.org, assistantId, dispatch, setValues]);

  return (
    <div>
      <div className="row">
        <div className="card border p-0">
          <div className="card-header p-4 py-4 border-bottom bg-body d-flex justify-content-between align-items-center">
            <h3 className="mb-0 d-flex align-items-center">
              Assistant Details
            </h3>
          </div>

          <div className="card-body">
            <div className="col-12">
              <form onSubmit={formik.handleSubmit}>
                <div className="mb-4">
                  <label className="fw-bold mb-2">
                    Assistant Name <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    className={`form-control ${
                      formik.touched.assistantName &&
                      formik.errors.assistantName
                        ? "is-invalid"
                        : ""
                    }`}
                    name="assistantName"
                    placeholder="Enter assistant name"
                    value={formik.values.assistantName}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  />
                  {formik.touched.assistantName &&
                    formik.errors.assistantName && (
                      <div className="invalid-feedback">
                        {formik.errors.assistantName}
                      </div>
                    )}
                </div>

                {formik.values.agentType === "platform" && (
                  <div className="mb-4">
                    <label className="fw-bold mb-2">
                      Platform <span className="text-danger">*</span>
                    </label>
                    <select
                      className={`form-control ${
                        formik.touched.selectedPlatformUuid &&
                        formik.errors.selectedPlatformUuid
                          ? "is-invalid"
                          : ""
                      }`}
                      name="selectedPlatformUuid"
                      value={formik.values.selectedPlatformUuid}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                    >
                      <option value="">Select Platform</option>
                      {(platforms || [])?.map((platform) => (
                        <option key={platform.uuid} value={platform.uuid}>
                          {platform.name}
                        </option>
                      ))}
                    </select>
                    {formik.touched.selectedPlatformUuid &&
                      formik.errors.selectedPlatformUuid && (
                        <div className="invalid-feedback">
                          {formik.errors.selectedPlatformUuid}
                        </div>
                      )}
                  </div>
                )}

                <div className="d-flex justify-content-between">
                  <button
                    className="btn btn-outline-danger"
                    type="button"
                    onClick={handleDelete}
                  >
                    <i
                      className={`wb ${Icons("delete")} fs-8 me-2 align-middle`}
                    ></i>
                    Delete
                  </button>
                  <button
                    className="btn btn-primary"
                    type="submit"
                    disabled={formik.isSubmitting}
                  >
                    <i
                      className={`wb ${Icons("save")} fs-8 me-2 align-middle`}
                    ></i>
                    {formik.isSubmitting ? "Updating..." : "Update Assistant"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AssistantDetailsForm;
